import {Component, Input} from '@angular/core';
import {TuiIcon} from '@taiga-ui/core';

export type GateweeIconStyle =
  | 'primary'
  | 'outline'
  | 'secondary'
  | 'on-accent'
  | 'negative'
  | 'warning'
  | 'positive';

export type GateweeIconWeight =
  | 'thin'
  | 'light'
  | 'regular'
  | 'bold'
  | 'fill'
  | 'duotone';

/** Prefix for Phosphor Icons (`@phosphor-icons/web`). */
const phosphorIconPrefix = '@phi.';

/**
 * Gatewee Icon Component
 *
 * A wrapper for Taiga UI icons with predefined style variants, plus Phosphor
 * icons via the `@phi.<kebab-name>` pattern. Phosphor CSS is included in each
 * app’s `styles` in `project.json` (`libs/assets/css/phosphor.css`). PostCSS
 * skips Tailwind for that file.
 *
 * @example
 * <gatewee-icon icon="@tui.users" style="primary" />
 * <gatewee-icon icon="@tui.calendar-days" style="outline" />
 * <gatewee-icon icon="@phi.arrow-right" iconStyle="primary" />
 * <gatewee-icon icon="@phi.arrow-right" weight="bold" iconStyle="primary" />
 */
@Component({
  selector: 'gatewee-icon',
  standalone: true,
  imports: [TuiIcon],
  templateUrl: './gatewee-icon.component.html',
  styleUrls: ['./gatewee-icon.component.css'],
})
export class GateweeIconComponent {
  /** Icon: `@tui.[name]` (Taiga) or `@phi.[kebab-name]` (Phosphor) */
  @Input() icon = '';

  /** Phosphor icon weight (ignored for Taiga icons) */
  @Input() weight: GateweeIconWeight = 'regular';

  /** Icon style variant */
  @Input() iconStyle: undefined | GateweeIconStyle = undefined;

  /** Icon size (optional, defaults to inherit from parent) */
  @Input() size?: string;

  /** Additional CSS classes to apply to the icon */
  @Input() class?: string;

  get styleClass(): string {
    switch (this.iconStyle) {
      case 'primary':
        return 'icon-primary';
      case 'outline':
        return 'icon-outline';
      case 'secondary':
        return 'icon-secondary';
      case 'on-accent':
        return 'icon-on-accent';
      case 'negative':
        return 'icon-negative';
      case 'warning':
        return 'icon-warning';
      case 'positive':
        return 'icon-positive';
      default:
        return '';
    }
  }

  get combinedClasses(): string {
    const classes = [this.styleClass];
    if (this.class) {
      classes.push(this.class);
    }
    return classes.filter(Boolean).join(' ');
  }

  /** True when `icon` uses the Phosphor `@phi.*` namespace. */
  get isPhosphorIcon(): boolean {
    return this.icon.startsWith(phosphorIconPrefix);
  }

  /**
   * Phosphor kebab-case glyph name after `@phi.`, or `null` if missing/invalid.
   */
  get phosphorGlyphName(): string | null {
    if (!this.isPhosphorIcon) {
      return null;
    }
    const name = this.icon.slice(phosphorIconPrefix.length).trim();
    return name.length ? name : null;
  }

  /** Phosphor weight CSS class (`ph`, `ph-bold`, etc.). */
  get phosphorWeightClass(): string {
    return this.weight === 'regular' ? 'ph' : `ph-${this.weight}`;
  }

  /** Classes for `<i>`: weight + `ph-<name>` plus style/extra classes. */
  get phosphorFullClasses(): string {
    const glyph = this.phosphorGlyphName;
    if (!glyph) {
      return this.combinedClasses;
    }
    return [this.phosphorWeightClass, `ph-${glyph}`, this.combinedClasses]
      .filter(Boolean)
      .join(' ');
  }
}
